import { Tag } from 'antd'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { MdOutlineCalendarToday, MdOutlineFlag } from 'react-icons/md'

import { Form, FormField } from '@/Core/UI/Form'
import {
    EntityBody,
    EntityHeader,
    EntityLayout,
    EntityPanel,
    EntityTabs,
    FieldCell,
    FieldGrid,
    FieldSection,
    HeaderChip,
    SectionEmpty,
} from '@/Ship/Components/EntityCard'
import { describeDeadline } from '@/Ship/Helpers/fieldViews'

import { TaskCommentsTab } from '../Components/Tabs/TaskCommentsTab'
import { TaskSubtasksTab } from '../Components/Tabs/TaskSubtasksTab'
import { TaskHistoryTab } from '../Components/Tabs/TaskHistoryTab'
import { TaskTreeTab } from '../Components/Tabs/TaskTreeTab'
import { TaskFilesView } from '../Components/TaskFilesView'
import { STATUS_MAP } from '../Contracts/TaskStatus'

import type { ReactNode } from 'react'
import type { TaskCrudContextLike, TaskFormContextLike } from '../Contracts/Crud.types'
import type { TaskStatus } from '../Contracts/TaskStatus'

interface Props {
    crudContext?: TaskCrudContextLike
    formContext?: TaskFormContextLike
}

/**
 * Блоки вкладки «Загальне» — кожен відповідає на одне питання:
 *
 *  - **Головне** — що зробити: назва, опис, частиною чого є, файли;
 *  - **Учасники** — хто робить, хто ставить, хто стежить. Виконавці першими:
 *    це обов'язкове поле й перше, що шукають у чужому завданні;
 *  - **Виконання** — у якому стані й на коли: статус, пріоритет, дедлайн, а
 *    після завершення — коли завершили й скільки часу пішло. Дата завершення
 *    стоїть під дедлайном — їх порівнюють;
 *  - **Службова інформація** — хто й коли створив і змінив.
 *
 * Порядок полів у блоці — рядками по два, зліва направо.
 */
const SECTIONS = {
    main: ['title', 'description', 'parent'],
    people: ['responsibles', 'manager', 'supervisors'],
    progress: ['status', 'isPriority', 'deadline', 'completedAt', 'timeSpentInMinutes'],
    service: ['createdAt', 'createdBy', 'updatedAt', 'updatedBy'],
} as const

/** На всю ширину блоку: довгий текст у половині читається погано. */
const WIDE = new Set<string>(['title', 'description', 'parent', 'files'])

/** Проставляє сервер — у формі нового завдання їм нема чого показати. */
const SERVER_FIELDS = new Set<string>([
    'number',
    'status',
    'completedAt',
    'timeSpentInMinutes',
    ...SECTIONS.service,
])

/** Розкладені явно: у шапці, блоках чи окремим показом (файли). */
const PLACED = new Set<string>(['number', 'files', ...Object.values(SECTIONS).flat()])

/**
 * Шапка картки: номер, статус, пріоритет, дедлайн і назва — поточні значення.
 *
 * Окремим observer-компонентом свідомо: вона читає поля, які користувач саме
 * редагує, і на кожну літеру в назві має перемальовуватися лише вона, а не
 * вся форма (див. застереження в `TaskFormTemplate`).
 */
const TaskCardHeader = observer(({ formContext }: { formContext: TaskFormContextLike }) => {
    const values = formContext.values ?? {}
    const status = values.status as TaskStatus | undefined
    const statusInfo = status ? STATUS_MAP[status] : undefined
    const deadline = describeDeadline(values.deadline, status === 'completed')

    return (
        <EntityHeader
            code={typeof values.number === 'number' ? `№ ${values.number}` : undefined}
            status={statusInfo ? { label: statusInfo.label, color: statusInfo.color } : undefined}
            badges={
                values.isPriority ? (
                    <Tag
                        color="error"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginInlineEnd: 0 }}
                    >
                        <MdOutlineFlag />
                        Пріоритетне
                    </Tag>
                ) : null
            }
            aside={
                values.deadline ? (
                    <HeaderChip icon={<MdOutlineCalendarToday />} tone={deadline.tone} title="Дедлайн">
                        {deadline.text}
                    </HeaderChip>
                ) : undefined
            }
            {...(values.title ? { title: values.title } : {})}
        />
    )
})

/**
 * Шаблон картки завдання — ЄДИНИЙ для view/edit/create
 * (`detailConfig.modeConfig.{view,edit,create}.template`), щоб перегляд і
 * редагування виглядали однаково (запит замовника з UI-огляду 11).
 *
 * Розкладка — спільна для карток сутностей (правила — `EntityLayout`):
 *  - **шапка** — номер, статус, пріоритет, дедлайн із тим, скільки лишилося, і
 *    назва; під нею смуга вкладок;
 *  - **«Загальне»** — блоки полів (`SECTIONS`) ліворуч, коментарі праворуч;
 *  - **«Підзавдання»**, **«Історія змін»**, **«Дерево»** — пов'язане.
 *    Підзавдання — одразу за «Загальним» (вимога замовника: вони першими серед
 *    пов'язаного).
 *
 * У створенні немає шапки, вкладок і службових полів: «№ —», «Статус —» у
 * формі нового завдання — порожні рядки, що нічого не кажуть. На місці
 * коментарів — пояснення, коли вони з'являться.
 *
 * Поле `files`: у view й edit — власний `TaskFilesView` (ядровий FileInput не
 * дає видалення на view-картці, хвіст 08.1); у create — ядровий `FormField`.
 */
export const TaskFormTemplate = observer(({ crudContext, formContext }: Props) => {
    // Лічильники активацій вкладок: antd Tabs монтує вкладку один раз і тримає
    // змонтованою, тому її useEffect-фетч не повторюється — після Старт/Пауза
    // вкладка показувала застарілі дані. Зміна key → ремонт → свіжий фетч на
    // КОЖНЕ відкриття. Коментарі стоять на «Загальному» й не ремонтуються, щоб
    // не губити недописану чернетку при перемиканні вкладок.
    const [historyEpoch, setHistoryEpoch] = useState(0)
    const [treeEpoch, setTreeEpoch] = useState(0)
    const [subtasksEpoch, setSubtasksEpoch] = useState(0)

    // ⚠️ Значень полів, які користувач редагує, тут не читаємо — лише `uuid`.
    // observer перемальовує шаблон на кожну зміну прочитаного значення, а
    // ядровий `FormField` на кожне перемальовування створює свій внутрішній
    // компонент наново: усі поля форми перемонтовуються, блимають і гублять
    // фокус. Так і було, поки шапка читала тут назву, — друк у полі «Назва»
    // обривався на першій літері. Усе, що показує поточні значення, живе в
    // окремих observer-компонентах (`TaskCardHeader`).
    const fields = crudContext?.resource?.fields ?? {}
    // Через сам `formContext`, а не через `values`: за цією умовою TypeScript
    // звужує `formContext` для вкладок, яким він потрібен без `undefined`
    const withTabs = !!formContext?.values?.uuid
    const isNew = !withTabs

    // Майбутні поля, не розкладені явно, — окремим блоком, а не «в нікуди»
    const rest = Object.keys(fields).filter((name) => !PLACED.has(name))

    const cell = (name: string): ReactNode =>
        name in fields && !(isNew && SERVER_FIELDS.has(name)) ? (
            <FieldCell key={name} wide={WIDE.has(name)}>
                <FormField name={name} />
            </FieldCell>
        ) : null

    const grid = (names: readonly string[], extra?: ReactNode) => (
        <Form formContext={formContext}>
            <FieldGrid>
                {names.map(cell)}
                {extra}
            </FieldGrid>
        </Form>
    )

    // create: ядровий FileInput (завдання ще нема — attach на POST);
    // edit/view: наш TaskFilesView — робоче видалення наявних (N12, ядровий
    // FileInput б'є в @id без DELETE-роуту) + у edit зона додавання
    // (upload → values.files → attach трансформером)
    const files = isNew ? (
        cell('files')
    ) : (
        <FieldCell wide>
            <TaskFilesView formContext={formContext} crudContext={crudContext} />
        </FieldCell>
    )

    const sections = (
        <>
            <FieldSection title="Головне">{grid(SECTIONS.main, files)}</FieldSection>
            <FieldSection title="Учасники">{grid(SECTIONS.people)}</FieldSection>
            <FieldSection title="Виконання">{grid(SECTIONS.progress)}</FieldSection>
            <FieldSection title="Додатково" hidden={rest.length === 0}>
                {grid(rest)}
            </FieldSection>
            <FieldSection title="Службова інформація" hidden={isNew}>
                {grid(SECTIONS.service)}
            </FieldSection>
        </>
    )

    // Форма створення — тієї ж геометрії, що й картка: поля стоять там само,
    // де користувач потім їх шукатиме, а не розтягуються на всю шухляду
    if (!withTabs) {
        return (
            <EntityLayout>
                <EntityBody
                    main={sections}
                    aside={
                        <FieldSection title="Коментарі">
                            <SectionEmpty>Коментувати можна буде після збереження завдання.</SectionEmpty>
                        </FieldSection>
                    }
                />
            </EntityLayout>
        )
    }

    return (
        <EntityLayout>
            <EntityTabs
                header={<TaskCardHeader formContext={formContext} />}
                onChange={(key) => {
                    if (key === 'history') setHistoryEpoch((e) => e + 1)
                    if (key === 'tree') setTreeEpoch((e) => e + 1)
                    if (key === 'subtasks') setSubtasksEpoch((e) => e + 1)
                }}
                items={[
                    {
                        key: 'general',
                        label: 'Загальне',
                        children: (
                            <EntityBody
                                main={sections}
                                aside={
                                    <FieldSection title="Коментарі">
                                        <TaskCommentsTab formContext={formContext} />
                                    </FieldSection>
                                }
                            />
                        ),
                    },
                    {
                        key: 'subtasks',
                        label: 'Підзавдання',
                        children: (
                            <EntityPanel>
                                <TaskSubtasksTab key={subtasksEpoch} formContext={formContext} />
                            </EntityPanel>
                        ),
                    },
                    {
                        key: 'history',
                        label: 'Історія змін',
                        children: (
                            <EntityPanel>
                                <TaskHistoryTab key={historyEpoch} formContext={formContext} />
                            </EntityPanel>
                        ),
                    },
                    {
                        key: 'tree',
                        label: 'Дерево',
                        children: (
                            <EntityPanel>
                                <TaskTreeTab key={treeEpoch} formContext={formContext} />
                            </EntityPanel>
                        ),
                    },
                ]}
            />
        </EntityLayout>
    )
})
